import type { ConversaDetalhe, MensagemChat } from '@mymaxx/shared';
import { LevarAsConversas } from './LevarAsConversas';
import { useEffect, useState } from 'react';

/**
 * De quem é a bola e há quanto tempo. Só as falas de gente contam: mensagem do bot ou
 * do sistema ("Fulano entrou no atendimento") não é resposta a ninguém.
 */
export interface EsperaDaConversa {
  lado: 'cliente_esperando' | 'aguardando_cliente' | 'nenhum';
  segundos: number;
}

export function esperaDaConversa(mensagens: MensagemChat[], agora: number): EsperaDaConversa {
  const ultimaDe = (de: MensagemChat['de']) =>
    mensagens
      .filter((m) => m.de === de && m.tipo !== 'nota_interna' && m.tipo !== 'sussurro')
      .at(-1);

  const doCliente = ultimaDe('cliente');
  const doAtendente = ultimaDe('atendente');
  const emMs = (m?: MensagemChat) => (m ? new Date(m.criadaEm).getTime() : 0);

  if (!doCliente && !doAtendente) return { lado: 'nenhum', segundos: 0 };

  const clienteFalouPorUltimo = emMs(doCliente) > emMs(doAtendente);
  const referencia = clienteFalouPorUltimo ? emMs(doCliente) : emMs(doAtendente);
  return {
    lado: clienteFalouPorUltimo ? 'cliente_esperando' : 'aguardando_cliente',
    segundos: Math.max(0, Math.round((agora - referencia) / 1000)),
  };
}

/** Relógio de um segundo, para os contadores andarem sozinhos. */
export function useAgora(ativo = true): number {
  const [agora, setAgora] = useState(() => Date.now());
  useEffect(() => {
    if (!ativo) return;
    const t = setInterval(() => setAgora(Date.now()), 1000);
    return () => clearInterval(t);
  }, [ativo]);
  return agora;
}

export function duracaoCurta(segundos: number): string {
  const s = Math.max(0, Math.round(segundos));
  if (s < 60) return `${s}s`;
  const min = Math.floor(s / 60);
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60);
  return `${h}h${String(min % 60).padStart(2, '0')}`;
}

/** Cabeçalho do chat: cliente, protocolo, de quem é a bola e as ações do atendimento. */
export function CabecalhoConversa({
  conversa,
  acoes,
}: {
  conversa: ConversaDetalhe;
  acoes?: React.ReactNode;
}) {
  const encerrada = conversa.status === 'encerrada';
  const agora = useAgora(!encerrada);
  const espera = esperaDaConversa(conversa.mensagens, agora);

  return (
    <header className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-200 px-4 py-2.5 dark:border-neutral-800">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">
          {conversa.cliente ?? 'Ainda sem identificação'}
        </p>
        <p className="truncate font-mono text-[11px] text-neutral-500">
          {conversa.protocolo}
          <span className="font-sans"> · {conversa.setor?.nome ?? 'sem setor'}</span>
          {conversa.participantes.length > 1 && (
            <span className="font-sans">
              {' · '}
              {conversa.participantes.map((p) => p.nome.split(/\s+/)[0] ?? p.nome).join(' e ')}
            </span>
          )}
        </p>
      </div>

      <div className="flex items-center gap-3">
        {!encerrada && espera.lado !== 'nenhum' && (
          <span
            className={`rounded-full px-2 py-1 text-[11px] tabular-nums ${
              espera.lado === 'cliente_esperando'
                ? 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200'
                : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300'
            }`}
            title={
              espera.lado === 'cliente_esperando'
                ? 'O cliente falou por último e está esperando você'
                : 'Você falou por último; o cliente ainda não respondeu'
            }
          >
            {espera.lado === 'cliente_esperando' ? 'sem resposta há ' : 'aguardando cliente há '}
            {duracaoCurta(espera.segundos)}
          </span>
        )}
        <LevarAsConversas conversa={conversa} />
        {acoes}
      </div>
    </header>
  );
}
